import { CommonModule } from '@angular/common';
import { PwcsInlineSvgComponent } from 'src/app/shared/components/pwcs-inline-svg/pwcs-inline-svg.component';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ProgressComponent,
	PwcsLayoutComponent,
	TileComponent,
	ButtonComponent,
	FormGroupComponent,
	InputComponent,
} from '@tecalisdev/infinity';
import { ProgressService } from 'src/app/core/helpers/progress.service';
import { ReactiveFormsService } from 'src/app/core/helpers/reactive-forms.service';
import { LoadingService } from 'src/app/core/services/loading.service';
import { IMaster } from 'src/app/shared/models/master.models';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { IBankScoringUrlResponse } from 'src/app/user-identification/models/connection';
import {
	CONNECTION_TYPE,
	EVALUATION_TYPE,
	IConnectionOperation,
	IFinishOperation,
	IOperation,
	OPERATION_STATUS,
	OPERATION_TYPE,
} from 'src/app/user-identification/models/user-identification';
import { ConnectionsService } from 'src/app/user-identification/services/connections/connections.service';
import { ProcessService } from 'src/app/user-identification/services/process.service';

@Component({
	selector: 'app-bank-scoring',
	imports: [
		CommonModule,
		TranslatePipe,
		PwcsLayoutComponent,
		ProgressComponent,
		TileComponent,
		ButtonComponent,
		FormGroupComponent,
		InputComponent,
		ReactiveFormsModule,
		PwcsInlineSvgComponent,
	],
	templateUrl: './bank-scoring.component.html',
	styleUrls: ['./bank-scoring.component.scss'],
})
export class BankScoringComponent implements OnInit {
	private connectionsService = inject(ConnectionsService);
	private loadingService = inject(LoadingService);
	private trans = inject(TranslateService);
	public reactiveFormService = inject(ReactiveFormsService);
	private route = inject(ActivatedRoute);

	@Input()
	get operations(): IOperation[] {
		return this._operations;
	}
	set operations(operations: IOperation[]) {
		this._operations = operations;
		const chooseOperation = operations.find((op) => op.type === OPERATION_TYPE.CHOOSE);
		const bankOperation: any = operations.find(
			(op) => op.type === OPERATION_TYPE.CONNECTIONS && op.config?.type === CONNECTION_TYPE.BANK_SCORING,
		);
		/*
			- Si tengo la operacion choose no muestro el tutorial y realizo la
			comprobaciones por si tengo ya los datos necesarios e iniciar la operación
			- Si no tengo la operacion choose y tengo el campo que indica que se
			debe evaluar financieramiente el documento muestro el tutorial
		*/
		const q = this.route.snapshot.queryParams;
		if (q['bankredirect'] || q['uuid_operation']) {
			this.tutorial = false;
			return;
		}
		if (chooseOperation) {
			this.startBankConnection();
		} else {
			if (bankOperation?.config?.evaluation) {
				this.tutorial = true;
			} else {
				this.startBankConnection();
			}
		}
	}
	@Input() operation: IConnectionOperation;

	public showIbanForm: boolean = false;
	public showNameForm: boolean = false;
	public OPERATION_TYPE: any = OPERATION_TYPE;
	public EVALUATION_TYPE: any = EVALUATION_TYPE;
	public numberOfTries: number = 0;
	public errorType:
		| 'IBAN_NOT_FOUND'
		| 'CONSENT_NOT_FOUND'
		| 'TOKEN_NOT_FOUND'
		| 'NAME_NOT_FOUND'
		| 'BANK_NOT_FOUND'
		| 'USER_REJECTED'
		| 'SERVER';
	public bankScoringUrlResonse: IBankScoringUrlResponse;
	public tutorial: boolean = false;
	public formIban: FormControl = new FormControl('', [
		Validators.required,
		Validators.pattern('^[A-Z]{2}[0-9A-Z]{13,31}$'),
	]);
	public formName: FormGroup = new FormGroup({
		name: new FormControl('', [Validators.required]),
		surname: new FormControl('', [Validators.required]),
	});
	private _operations: IOperation[];
	private initOperation: boolean = false;

	constructor(
		public designService: DesignService,
		public progressService: ProgressService,
		private masterService: MasterService,
		private processService: ProcessService,
	) {}

	ngOnInit(): void {
		// Detect query params from bank redirect (e.g. ?bankredirect=1&statusAB=403&...)
		this.route.queryParams.subscribe((params) => {
			if (params?.['bankredirect']) {
				if (this.isConsentStatusValid(params?.['consentStatusAB'])) {
					this.getScoring();
				} else {
					this.errorType = 'USER_REJECTED';
				}
			} else {
				if (this.operation?.config?.evaluation) {
					this.tutorial = true;
				} else {
					this.startBankConnection();
				}
			}
		});

		this.masterService.getMaster().subscribe((master: IMaster) => {
			if (master && !this.initOperation) {
				this.initOperation = true;
				this.processService.initOperation(this.operation.type);
			}
		});
	}

	public generateUrl(iban?: string): void {
		this.showIbanForm = false;
		this.showNameForm = false;
		this.errorType = null;
		this.loadingService.show(this.trans.instant('CONNECTIONS.BANK_SCORING.LOADING.GENERATING_CONNECTION'));
		this.connectionsService
			.bankScoringUrl({
				uuid_config: this.operation.uuid,
				iban: iban,
			})
			.subscribe({
				next: (response: IBankScoringUrlResponse) => {
					window.open(response.url, '_self');
				},
				error: (error: HttpErrorResponse) => {
					this.handleBankConnectionHttpError(error);
					this.loadingService.hide();
				},
				complete: () => {},
			});
	}

	public getScoring(name?: string, surname?: string): void {
		this.showIbanForm = false;
		this.showNameForm = false;
		this.errorType = null;
		this.numberOfTries++;
		this.loadingService.show(this.trans.instant('CONNECTIONS.BANK_SCORING.LOADING.VERIFYING_DATA'));
		this.connectionsService
			.bankScoring({
				operation_uuid: this.getUuidOperationFromUrl(),
				name: name,
				surname: surname,
			})
			.subscribe({
				next: (response: any) => {
					if (response.result === OPERATION_STATUS.KO) {
						this.operations.forEach((op: IOperation) => {
							if (op.type !== OPERATION_TYPE.FINISH) {
								op.completed = true;
							}
						});
					}
					const found = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
					if (found) {
						(found as IFinishOperation).config.status = response.result;
					}
					this.operation.completed = true;
				},
				error: (error: HttpErrorResponse) => {
					this.handleBankConnectionHttpError(error);
					this.loadingService.hide();
				},
				complete: () => {
					this.loadingService.hide();
				},
			});
	}

	private handleBankConnectionHttpError(error: HttpErrorResponse): void {
		const retry = error?.error?.retry ?? false;
		if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
			this.errorType = null;
			this.verificationFail(error);
			return;
		}
		this.setErrorType(error);
		this.verificationFail(error);
	}

	private verificationFail(error: HttpErrorResponse): void {
		const retry = error?.error?.retry ?? false;
		if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
			this.operation.completed = true;
			this.operations.forEach((op: IOperation) => {
				if (op.type !== OPERATION_TYPE.FINISH) {
					op.completed = true;
				}
			});
			const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
			if (foundFinish) {
				(foundFinish as IFinishOperation).config.status = error?.error?.result;
			}
		}
	}

	private setErrorType(error: HttpErrorResponse): void {
		switch (error?.error?.error?.error_code) {
			case 1115:
				this.errorType = 'IBAN_NOT_FOUND';
				break;
			case 1116:
				this.errorType = 'CONSENT_NOT_FOUND';
				break;
			case 1117:
				this.errorType = 'TOKEN_NOT_FOUND';
				break;
			case 1118:
				this.errorType = 'NAME_NOT_FOUND';
				break;
			case 1119:
				this.errorType = 'BANK_NOT_FOUND';
				break;
			default:
				this.errorType = 'SERVER';
				break;
		}
	}

	public ibanForm(): void {
		this.errorType = null;
		this.showIbanForm = true;
	}

	public nameForm(): void {
		this.errorType = null;
		this.showNameForm = true;
	}

	public validateIbanAndGenerateUrl(): void {
		if (this.formIban.valid) {
			this.generateUrl(this.formIban.value);
			return;
		}
		this.formIban.markAsTouched();
	}

	public validateNameAndGetScoring(): void {
		if (this.formName.valid) {
			this.getScoring(this.formName.get('name').value, this.formName.get('surname').value);
			return;
		}
		this.formName.markAllAsTouched();
	}

	public startBankConnection(): void {
		this.tutorial = false;
		this.generateUrl();
	}

	private isConsentStatusValid(consent: string | string[]): boolean {
		return Array.isArray(consent) ? consent[consent.length - 1].includes('valid') : consent === 'valid';
	}

	private getUuidOperationFromUrl(): string {
		const v = this.route.snapshot.queryParams['uuid_operation'];
		if (v == null) {
			return '';
		}
		return Array.isArray(v) ? v[v.length - 1] : v;
	}
}
